/*
Bulk action toolbar for message operations
Provides selection controls and bulk discard functionality
Author: Implementation for bulk discard feature
Date: 2025-01-15
*/
import React, { useState } from 'react'
import { Button, Flex, Text, Dialog } from '@radix-ui/themes'
import { TrashIcon, Cross1Icon } from '@radix-ui/react-icons'

interface BulkMessageActionsProps {
  selectedCount: number
  totalCount: number
  onSelectAll: () => void
  onClearSelection: () => void
  onBulkDiscard: () => Promise<void>
  isLoading: boolean
  disabled?: boolean
}

export const BulkMessageActions: React.FC<BulkMessageActionsProps> = ({
  selectedCount,
  totalCount,
  onSelectAll,
  onClearSelection,
  onBulkDiscard,
  isLoading,
  disabled = false,
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const handleBulkDiscard = async () => {
    setShowConfirmDialog(false)
    await onBulkDiscard()
  }

  if (disabled) {
    return null
  }

  return (
    <>
      <Flex
        direction="row"
        gap="3"
        align="center"
        p="3"
        style={{
          backgroundColor: selectedCount > 0 ? '#E6F4FF' : '#F8F9FA',
          borderRadius: '6px',
          border: '1px solid #E1E5E9',
        }}
      >
        <Flex gap="2" align="center">
          <Button
            variant="soft"
            size="2"
            onClick={selectedCount === totalCount ? onClearSelection : onSelectAll}
            disabled={totalCount === 0}
          >
            {selectedCount === totalCount ? 'Deseleccionar Todo' : 'Seleccionar Todo'}
          </Button>

          {selectedCount > 0 && (
            <Button variant="soft" size="2" onClick={onClearSelection}>
              <Cross1Icon />
              Limpiar
            </Button>
          )}
        </Flex>

        <Text size="2" color="gray">
          {selectedCount > 0
            ? `${selectedCount} de ${totalCount} mensajes seleccionados`
            : `${totalCount} mensajes disponibles`}
        </Text>

        <Flex ml="auto">
          {selectedCount > 0 && (
            <Button
              color="red"
              variant="solid"
              size="2"
              onClick={() => setShowConfirmDialog(true)}
              disabled={isLoading}
              loading={isLoading}
            >
              <TrashIcon />
              Descartar Seleccionados ({selectedCount})
            </Button>
          )}
        </Flex>
      </Flex>

      {/* Confirmation Dialog */}
      <Dialog.Root open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <Dialog.Content maxWidth="450px">
          <Dialog.Title>Confirmar Descarte</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            ¿Estás seguro de que deseas descartar {selectedCount} mensaje{selectedCount > 1 ? 's' : ''}? Esta acción no
            se puede deshacer.
          </Dialog.Description>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancelar
              </Button>
            </Dialog.Close>
            <Button color="red" onClick={handleBulkDiscard} disabled={isLoading} loading={isLoading}>
              Confirmar Descarte
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </>
  )
}
